/**
 * ConsentGate — shown to first-time visitors and users who haven't accepted
 * Lapathoniia's Terms of Service, Privacy Policy, and Cookie Policy.
 *
 * Stores acceptance in localStorage under 'lapa_consent_v1'.
 * Must be accepted before accessing the chat.
 */
import { useState } from 'react';

const CONSENT_KEY = 'lapa_consent_v1';

export function hasAcceptedConsent(): boolean {
  try {
    return localStorage.getItem(CONSENT_KEY) === '1';
  } catch {
    return false;
  }
}

function recordConsent() {
  try {
    localStorage.setItem(CONSENT_KEY, '1');
  } catch {}
}

const gold = '#f4c542';
const cream = '#f8f0da';
const bg = '#0e0e14';
const bgCard = '#15151f';
const border = 'rgba(244,197,66,0.15)';
const teal = '#4ecdc4';

export default function ConsentGate({ onAccept }: { onAccept: () => void }) {
  const [checked, setChecked] = useState(false);

  const handleAccept = () => {
    if (!checked) return;
    recordConsent();
    onAccept();
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: bg,
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      padding: '16px',
      fontFamily: "'Inter', 'Segoe UI', sans-serif",
    }}>
      <div style={{
        background: bgCard,
        border: `1px solid ${border}`,
        borderRadius: '16px',
        padding: '32px 28px',
        maxWidth: '520px',
        width: '100%',
        boxShadow: '0 8px 40px rgba(0,0,0,0.6)',
      }}>
        {/* Logo + title */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <img
            src="/lapathoniia-logo.png"
            alt="Lapathoniia"
            height={36}
            style={{ flexShrink: 0, filter: 'drop-shadow(0 0 6px rgba(59,130,246,0.4))' }}
          />
          <div>
            <div style={{ color: gold, fontSize: '17px', fontWeight: 700, letterSpacing: '-0.3px' }}>
              Lapathoniia
            </div>
            <div style={{ color: cream, opacity: 0.6, fontSize: '12px' }}>
              Українська AI-платформа
            </div>
          </div>
        </div>

        {/* Description */}
        <p style={{ color: cream, opacity: 0.85, fontSize: '14px', lineHeight: 1.6, marginBottom: '24px' }}>
          Тут Ви можете спілкуватися з українськими мовними моделями LapaLLM та MamayLM з розширеними
          можливостями пошуку, аналізу документів та роботи з даними.
        </p>

        {/* Consent checkbox */}
        <label style={{
          display: 'flex', gap: '12px', alignItems: 'flex-start',
          cursor: 'pointer', marginBottom: '28px',
        }}>
          <input
            type="checkbox"
            checked={checked}
            onChange={e => setChecked(e.target.checked)}
            style={{
              marginTop: '3px', flexShrink: 0,
              width: '18px', height: '18px',
              accentColor: gold, cursor: 'pointer',
            }}
          />
          <span style={{ color: cream, opacity: 0.8, fontSize: '13px', lineHeight: 1.65 }}>
            Я погоджуюсь із{' '}
            <PolicyLink href="https://lapathoniia.top/terms">Загальними умовами використання</PolicyLink>,
            {' '}ознайомлений(а) із{' '}
            <PolicyLink href="https://lapathoniia.top/privacy">Політикою обробки персональних даних</PolicyLink>
            {' '}та{' '}
            <PolicyLink href="https://lapathoniia.top/cookies">Політикою використання файлів cookie</PolicyLink>
            {' '}і підтверджую, що мені виповнилося 14 років.{' '}
            <span style={{ opacity: 0.65 }}>
              Якщо Ви є користувачем з України віком від 14 до 18 років, проставленням відповідної позначки
              Ви підтверджуєте, що отримали попередню згоду батьків або законних представників.
            </span>
          </span>
        </label>

        {/* Accept button */}
        <button
          onClick={handleAccept}
          disabled={!checked}
          style={{
            width: '100%',
            padding: '13px 24px',
            background: checked ? gold : 'rgba(244,197,66,0.15)',
            color: checked ? '#0e0e14' : cream,
            border: 'none',
            borderRadius: '10px',
            fontSize: '15px',
            fontWeight: 700,
            cursor: checked ? 'pointer' : 'default',
            transition: 'background 0.15s, color 0.15s',
            letterSpacing: '-0.2px',
          }}
        >
          Продовжити →
        </button>

        {/* Footer links */}
        <div style={{
          display: 'flex', gap: '16px', justifyContent: 'center',
          marginTop: '20px', flexWrap: 'wrap',
        }}>
          {[
            ['Умови', 'https://lapathoniia.top/terms'],
            ['Конфіденційність', 'https://lapathoniia.top/privacy'],
            ['Cookies', 'https://lapathoniia.top/cookies'],
          ].map(([label, href]) => (
            <a key={href} href={href} target="_blank" rel="noopener noreferrer"
              style={{ color: teal, fontSize: '12px', opacity: 0.7, textDecoration: 'none' }}
            >
              {label}
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

function PolicyLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: teal, textDecoration: 'underline', textUnderlineOffset: '2px' }}
    >
      {children}
    </a>
  );
}
